/**
 * Backend-safe contextual facet contract between Marketplace and Search.
 * Indexed fields are resolved from allowlisted configuration before execution.
 */
import { CollatorType } from '../collators';

/**
 * Normalized filter clause for engine execution
 * Field is allowlisted and never client-controlled
 */
export interface FacetEngineFilterClause {
  field: string; // allowlisted indexed path
  operator: string; // validated operator
  values: unknown[]; // normalized
}

/**
 * Definition of one facetable field, resolved from configuration
 */
export interface FacetDefinitionExecution {
  id: string; // public stable facet ID
  field: string; // allowlisted search index field
  type: string; // determines operator/value validation
  definitionOrder: number; // preserves discovery order
  applicableDataLandscapes?: string[]; // empty/absent means all
}

/**
 * Request sent to the Search engine for contextual facet aggregation
 */
export interface FacetEngineQuery {
  collatorType: CollatorType.MARKETPLACE_PROJECTS; // fixed
  environment: string; // selected environment
  term: string; // normalized full-text query (may be empty)
  filters: FacetEngineFilterClause[]; // allowlisted indexed clauses
  facets: FacetDefinitionExecution[]; // all facetable defs or exactly one focused
  limit: number; // effective page size
  after?: { displayValue: string; rawValue: string }; // optional keyset continuation
}

/**
 * Ordered facet values returned by the engine
 */
export interface FacetEngineValue {
  value: string; // exact raw indexed value
  displayValue: string; // same as raw value
  count: number; // exact distinct matching documents
}

/**
 * One page of values for a facet
 */
export interface FacetEngineResult {
  id: string; // facet ID from execution
  values: FacetEngineValue[]; // at most limit values
  hasMore: boolean; // true if limit+1 returned by database
}

/**
 * Complete response from facet aggregation
 */
export interface FacetEngineResponse {
  facets: FacetEngineResult[]; // all requested facets or exactly one focused
}

/**
 * Error thrown when the Search engine cannot fulfill a request
 */
export class FacetEngineError extends Error {
  constructor(
    public code:
      | 'unavailable'
      | 'invalid_collator'
      | 'invalid_field'
      | 'invalid_operator'
      | 'query_error'
      | 'internal_error',
    message: string,
  ) {
    super(message);
    this.name = 'FacetEngineError';
  }
}

/**
 * Contract that every WitboostSearchEngine must implement
 */
export interface IFacetAggregationEngine {
  /**
   * Execute a contextual facet aggregation
   * @param query The facet engine query with normalized context
   * @returns Aggregated facets or throws FacetEngineError
   */
  queryContextualFacets(query: FacetEngineQuery): Promise<FacetEngineResponse>;
}
